import { useState, useCallback } from 'react';
import { useTracking } from 'react-tracking';
import { RouterLink } from '@redux-first-router-link';
import loadable from '@loadable/component';

import {
  Grid,
  Box,
  TabList,
  Tab,
  Button,
  useTheme,
  Text,
  ScrollPanelWithoutScrollbars,
} from '@simplywallst/ui-core';
import OpenLinkOutline from '@simplywallst/ui-core/icons/OpenLinkOutline';

import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import { ROUTE_MY_PORTFOLIOS } from '@/constants/routes';

import { formatterPercentage } from '@/utilities/format';
import { pluralise } from '@/utilities/pluralise';
import { formatCurrencyToScaleTruncated } from '@/utilities/portfolio/formatValue';

import type { AggregatePortfolio as AggregatePortfolioType } from '@/hooks/portfolio/useAggregatePortfolio';
import { useIsMobileScreen } from '@/hooks/useMedia';
import useUser from '@/hooks/useUser';

import { KeyMetric } from '@/components/KeyMetric';
import Tooltip from '@/components/Tooltip';
import PositiveNegativeValue from '@/components/PositiveNegativeValue';
import { MinimalDropdown } from '@/components/Dropdown';
import { AggregatePortfolioPerformanceChart } from '@components/PortfolioPerformaceChart';
import { AggregatePortfolioChangeCurrency } from '@/components/AggregatePortfolioChangeCurrency';
import type { PerformancePeriod } from '@/components/PortfolioPerformaceChart/types';

import { getKeyMetricColor } from './utils';

import { StyledDiv, StyledText } from './styled';

const LimitedPortfolioBanner = loadable(
  () => import('./components/LimitedPortfolioBanner'),
  {
    resolveComponent: (c) => c.LimitedPortfolioBanner,
  }
);

const PortfolioDropdown = loadable(
  () => import('./components/PortfolioDropdown'),
  {
    resolveComponent: (c) => c.PortfolioDropdown,
  }
);

const period: PerformancePeriod[] = ['7D', '1M', '3M', 'YTD', '1Y'];

const isPeriod = (value: unknown): value is PerformancePeriod => {
  return period.includes(value as PerformancePeriod);
};

type Props = {
  aggregatePortfolio: AggregatePortfolioType;
  selectedPortfolioId: null;
  setSelectedPortfolioId: (id: string | null) => void;
};

export const AggregatePortfolio = ({
  aggregatePortfolio,
  selectedPortfolioId,
  setSelectedPortfolioId,
}: Props) => {
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'aggregatePortfolioOverview',
  });
  const theme = useTheme();
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  const hasAnalysisRestricted =
    aggregatePortfolio.analysis.isAnalysisRestricted;

  const { mutate: mutateUser } = useUser();

  const [performacePeriod, setPerformancePeriod] =
    useState<PerformancePeriod>('1Y');

  const holdingOrHoldings = pluralise(
    aggregatePortfolio.analysis.holdingsCount,
    'holding',
    'holdings'
  );

  const handleSelect = useCallback(
    (portfolioId) => {
      mutateUser({
        settings: {
          portfolio: {
            dashboard_default_portfolioId: portfolioId,
          },
        },
      });
      setSelectedPortfolioId(portfolioId);
    },
    [mutateUser, setSelectedPortfolioId]
  );

  const handlePeriodSelect = (option: unknown) => {
    if (!isPeriod(option)) return;
    setPerformancePeriod(option);
    trackEvent({
      action: 'click',
      modifier: 'changePerformancePeriod',
      period: option,
    });
  };

  return (
    <Track>
      <Grid
        data-cy-id="dashboard-portfolios-section"
        gapLg="4px"
        backgroundColor="noir"
        borderRadiusLg="x1"
      >
        <StyledDiv>
          <Grid
            padding="x2"
            paddingTopLg="x1_5"
            paddingBottomLg="x2"
            paddingLeftLg="x3"
            paddingRightLg="x3"
            gap="8px"
          >
            <Grid
              templateColumns="auto auto"
              alignItems="start"
              alignItemsMd="center"
              justifyContent="space-between"
            >
              <Grid
                templateColumns="auto"
                templateColumnsMd={
                  hasAnalysisRestricted
                    ? 'minmax(auto, 40%) max-content'
                    : 'auto'
                }
                alignItems="center"
              >
                <PortfolioDropdown
                  portfolioId={selectedPortfolioId}
                  onSelect={handleSelect}
                />
                {hasAnalysisRestricted ? (
                  <LimitedPortfolioBanner content="Some portfolios are limited due to your plan." />
                ) : null}
              </Grid>
              <Grid templateColumns="auto auto" alignItems="center">
                <RouterLink
                  to={{ type: ROUTE_MY_PORTFOLIOS }}
                  noUnderline
                  onClick={() => {
                    trackEvent({
                      action: 'click',
                      modifier: 'viewMyPortfolios',
                    });
                  }}
                >
                  <Button styleType="passive">
                    <OpenLinkOutline
                      fill={theme.color.whiteRGBA(0.5)}
                      aria-label="View portfolio"
                    />
                  </Button>
                </RouterLink>
                {aggregatePortfolio.analysis.currencyISO && (
                  <AggregatePortfolioChangeCurrency
                    currencyISO={aggregatePortfolio.analysis.currencyISO}
                  />
                )}
              </Grid>
            </Grid>
            <ScrollPanelWithoutScrollbars overflow="auto">
              <Grid templateColumns="repeat(3, max-content)" gap="32px">
                <KeyMetric>
                  <Text typography="xLargeStrong" data-cy-id="total-value">
                    {formatCurrencyToScaleTruncated(
                      aggregatePortfolio.analysis.value,
                      aggregatePortfolio.analysis.currencyISO || 'USD'
                    )}
                  </Text>
                  <Text typography="tiny" color="whiteRGBA" opacity={0.7}>
                    <Grid templateColumns="max-content auto" gap="4px">
                      <Tooltip
                        title={
                          <Box maxWidth="260px" padding="x1">
                            <Text
                              textAlign="center"
                              typography="tiny"
                              color="spaceGray"
                            >
                              This represents the combined worth of all your
                              stock portfolios, providing you with a
                              comprehensive view of your overall stock
                              investments.
                            </Text>
                          </Box>
                        }
                        light
                        placement="top"
                        zIndex={99999}
                      >
                        <StyledText>
                          <Text
                            as="span"
                            typography="tiny"
                            color="whiteRGBA"
                            opacity={0.7}
                          >
                            Total Value
                          </Text>
                        </StyledText>
                      </Tooltip>
                      • {aggregatePortfolio.analysis.holdingsCount}{' '}
                      {holdingOrHoldings}
                    </Grid>
                  </Text>
                </KeyMetric>
                <KeyMetric
                  color={getKeyMetricColor(aggregatePortfolio.analysis.roi)}
                >
                  <Text typography="xLargeStrong" data-cy-id="roi-value">
                    {formatCurrencyToScaleTruncated(
                      aggregatePortfolio.analysis.roiValue,
                      aggregatePortfolio.analysis.currencyISO || 'USD'
                    )}
                  </Text>
                  <Text typography="tiny" color="whiteRGBA" opacity={0.7}>
                    <Grid templateColumns="max-content auto" gap="4px">
                      <Tooltip
                        title={
                          <Box maxWidth="260px" padding="x1">
                            <Text
                              textAlign="center"
                              typography="tiny"
                              color="spaceGray"
                            >
                              This indicates the cumulative unrealised profits
                              or losses from all your stock portfolios. It
                              summarises the overall financial performance of
                              your current investments.
                            </Text>
                          </Box>
                        }
                        light
                        placement="top"
                        zIndex={99999}
                      >
                        <StyledText>
                          <Text
                            as="span"
                            typography="tiny"
                            color="whiteRGBA"
                            opacity={0.7}
                          >
                            Total Returns
                          </Text>
                        </StyledText>
                      </Tooltip>
                      <PositiveNegativeValue
                        value={aggregatePortfolio.analysis.roi}
                      >
                        {formatterPercentage(aggregatePortfolio.analysis.roi)}
                      </PositiveNegativeValue>
                    </Grid>
                  </Text>
                </KeyMetric>
                <KeyMetric color="darkerGray">
                  <Grid
                    templateColumns="max-content max-content"
                    gap="4px"
                    alignItems="end"
                  >
                    <Text typography="xLargeStrong">
                      {formatCurrencyToScaleTruncated(
                        aggregatePortfolio.analysis.estimatedYearlyDividend,
                        aggregatePortfolio.analysis.currencyISO || 'USD'
                      )}
                    </Text>
                    <Text
                      typography="baseStrong"
                      color="whiteRGBA"
                      opacity={0.5}
                    >
                      /yr
                    </Text>
                  </Grid>
                  <Text typography="tiny" color="whiteRGBA" opacity={0.7}>
                    <Grid templateColumns="max-content auto" gap="4px">
                      <Tooltip
                        title={
                          <Box maxWidth="260px" padding="x1">
                            <Text
                              textAlign="center"
                              typography="tiny"
                              color="spaceGray"
                            >
                              This figure represents the average dividend yield
                              expected from all your portfolios. It provides an
                              estimate of the combined yearly income you can
                              potentially earn from your portfolios based on the
                              dividend payments of the underlying stocks.
                            </Text>
                          </Box>
                        }
                        light
                        placement="top"
                        zIndex={99999}
                      >
                        <StyledText>
                          <Text
                            as="span"
                            typography="tiny"
                            color="whiteRGBA"
                            opacity={0.7}
                          >
                            Est. Dividend
                          </Text>
                        </StyledText>
                      </Tooltip>
                      •{' '}
                      {formatterPercentage(
                        aggregatePortfolio.analysis.estimatedDividendYield
                      )}{' '}
                      Yield
                    </Grid>
                  </Text>
                </KeyMetric>
              </Grid>
            </ScrollPanelWithoutScrollbars>
          </Grid>
        </StyledDiv>
        <Box
          position="relative"
          padding="x2"
          paddingTopLg="x2"
          paddingBottomLg="x2"
          paddingRightLg="x3"
          paddingLeftLg="x3"
        >
          {isMobileScreen ? (
            <Box
              position="absolute"
              top="14px"
              right="24px"
              backgroundColor="noir"
              padding="x0_5"
              borderRadius="x1"
            >
              <MinimalDropdown
                displayType="inline-block"
                name="period-dropdown"
                options={period.map((n) => ({ label: n, option: n }))}
                onSelect={handlePeriodSelect}
                label={performacePeriod}
                value={performacePeriod}
                underline={false}
              />
            </Box>
          ) : (
            <Box
              position="absolute"
              right="16px"
              width="248px"
              backgroundColor="noir"
              padding="x0_5"
              borderRadius="x1"
            >
              <TabList templateColumns="repeat(5, auto)">
                {period.map((n) => (
                  <Tab
                    key={`period-tab-${n}`}
                    styleType="period"
                    selected={performacePeriod === n}
                    onClick={() => handlePeriodSelect(n)}
                    value={n}
                  >
                    {n}
                  </Tab>
                ))}
              </TabList>
            </Box>
          )}
          <AggregatePortfolioPerformanceChart
            market={aggregatePortfolio.analysis.marketCountryISO || 'us'}
            period={performacePeriod}
          />
        </Box>
      </Grid>
    </Track>
  );
};
